import {renderCalendarContainer, renderTimeSlot, renderQuickSlotRow} from "../utils/templates";
import {t} from "../i18n";
import {isNonNullObject} from "../utils/helpers.js";
import {holdSlot} from "../utils/slotHold.js";
import {showSlotTakenAlert} from "../utils/alertDialog.js";

const QUICK_DAYS_LIMIT = 10;
const QUICK_DAYS_INITIAL = 4;
// Hour-grid range for the from-time popover. Adjust here if accounts work
// later/earlier than expected.
const HOUR_GRID_MIN = 7;
const HOUR_GRID_MAX = 22;
const MINUTE_GRID = [0, 15, 30, 45];
// Cat de des intrebam serverul, cat clienta sta pe lista de ore, daca vreo ora a fost luata / tinuta
// intre timp de altcineva (rezervarea temporara). O cerere mica; doar cu pagina vizibila.
const SLOT_POLL_MS = 10000;

export default class CalendarStep {
    constructor() {
        this.id = "calendar";
        this.title_key = "choose_appointment_time";
    }

    init({$, state, data, emit, api}) {
        this.$ = $;
        this.state = state;
        this.data = data;
        this.emit = emit;
        this.api = api;
        this.holding = false;
        this.polling = false;
        this.pollTimer = null;
        // Honor bwData.config.quick_slots — truthy = quick view, falsy = legacy
        // flatpickr calendar. Truthy check (not `!== false`) because the value
        // comes from a TINYINT(1) DB column with no boolean cast, so PHP emits
        // it as integer 0/1 — `0 !== false` is true in JS and would silently
        // ignore the admin toggle.
        this.quickEnabled = !!data.config?.quick_slots;
        this.viewMode = this.quickEnabled ? 'quick' : 'calendar';
        this.flatpickrRendered = false;
        this.dates = [];
        // Tracks whether `dates` is the FULL list (datesAvailability response).
        // In batched-quick mode it only holds the first N dates until the user
        // toggles to the full calendar — then we lazy-fetch the full list.
        this.datesLoaded = false;
        this.quickDays = [];
        this.fromHour = null; // null = bucket mode; "HH:MM" = chronological filter
        this.selected = state.selectedEmployee || null;
    }

    render(container) {
        const $ = this.$;
        const html = renderCalendarContainer({ quickSlots: this.quickEnabled });
        container.html(html);
        this.container = container;
        this.$widgetRoot = container.closest('.booking-widget');
        this.$calendarRow = $('.step-calendar-row', this.container);
        this.$spinner = $('.spinner-overlay', this.container);
        this.$quickPane = $('.bw-quick-slots-pane', this.container);
        this.$calendarPane = $('.bw-calendar-pane', this.container);
        this.$quickList = $('.bw-quick-slots-list', this.container);
        this.$quickEmpty = $('.bw-quick-empty', this.container);
        this.$quickHeader = $('.bw-quick-header', this.container);
        this.$quickTitle = $('.bw-quick-title', this.container);
        this.$quickFilterWrap = $('.bw-quick-filter-wrap', this.container);
        this.$toggle = $('.bw-quick-toggle', this.container);
        this.$confirm = $('.bw-shared-confirm', this.container);
        this.$noCal = $('.step-no-calendar', this.container);

        if (this.quickEnabled) {
            this.$seeMoreRow  = $('.bw-quick-see-more-row', this.container);
            this.$seeMoreBtn  = $('.bw-quick-see-more', this.container);
            this.$seeCalRow   = $('.bw-quick-see-cal-row', this.container);
            this.$seeCalBtn   = $('.bw-quick-see-cal', this.container);
            this.$fromText     = $('.bw-quick-from-text', this.container);
            this.$fromBtn      = $('.bw-quick-from-btn', this.container);
            this.$fromClear    = $('.bw-quick-from-clear', this.container);
            this.$fromPop      = $('.bw-quick-from-pop', this.container);
            this.$fromBackdrop = $('.bw-quick-from-backdrop', this.container);
            this.$fromHours    = $('.bw-quick-from-hours', this.container);
            this.$fromMinutes  = $('.bw-quick-from-minutes', this.container);
            this.$emptyLink    = $('.bw-quick-empty-link', this.container);

            this.buildHourMinuteGrids();

            this.$toggle.on('click', () => this.togglePane());

            this.$quickList.on('click', '.bw-quick-chip:not(.bw-quick-chip--empty):not(.bw-quick-chip--taken)', (e) => {
                const $chip = $(e.currentTarget);
                this.selectQuickChip({
                    $chip,
                    date:    $chip.data('date'),
                    startAt: $chip.data('start'),
                    endAt:   $chip.data('end')
                });
            });

            this.$seeMoreBtn.on('click', () => {
                this.$quickList.removeClass('is-collapsed');
                this.$seeMoreRow.hide();
                this.$seeCalRow.css('display', 'flex');
            });

            this.$seeCalBtn.on('click', () => this.togglePane());
            this.$emptyLink.on('click', () => this.togglePane());

            this.$fromBtn.on('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!this.fromHour) {
                    this.setFromHour(this.computeDefaultFromHour());
                }
                this.toggleFromPop(true);
            });

            this.$fromClear.on('click', (e) => {
                e.stopPropagation();
                e.preventDefault();
                this.setFromHour(null);
                this.toggleFromPop(false);
            });

            this.$fromHours.on('click', '.bw-quick-from-cell', (e) => {
                const hh = String($(e.currentTarget).data('value')).padStart(2, '0');
                const mm = (this.fromHour || '00:00').slice(3, 5);
                this.setFromHour(`${hh}:${mm}`);
                this.highlightFromGrid();
            });

            this.$fromMinutes.on('click', '.bw-quick-from-cell', (e) => {
                const mm = String($(e.currentTarget).data('value')).padStart(2, '0');
                const hh = (this.fromHour || '00:00').slice(0, 2);
                this.setFromHour(`${hh}:${mm}`);
                this.highlightFromGrid();
                // Minute selection commits — close the modal.
                this.toggleFromPop(false);
            });

            this.$fromBackdrop.on('click', () => this.toggleFromPop(false));
        }

        // Shared confirm
        this.$confirm.on('click', () => this.next());

        this.$noCal.hide();
        this.getAvailableDates();
        this.startSlotPolling();
    }

    togglePane() {
        if (this.viewMode === 'quick') {
            this.viewMode = 'calendar';
            this.$quickPane.hide();
            this.$calendarPane.show();
            // Header (now outside the panes) stays visible; hide quick-only
            // controls and flip the toggle label so the user can come back.
            if (this.$quickTitle) this.$quickTitle.hide();
            if (this.$quickFilterWrap) this.$quickFilterWrap.hide();
            this.$toggle.text(t('back_to_quick_slots'));
            this.$confirm.appendTo(this.$calendarRow);
            if (!this.flatpickrRendered) {
                this.ensureDatesLoaded(() => {
                    if (this.dates.length > 0) {
                        this.renderFlatpickr(this.dates);
                        this.flatpickrRendered = true;
                    }
                });
            }
            this.refreshConfirmState();
        } else {
            this.viewMode = 'quick';
            this.$calendarPane.hide();
            this.$quickPane.show();
            if (this.$quickTitle) this.$quickTitle.show();
            if (this.$quickFilterWrap) this.$quickFilterWrap.show();
            this.$toggle.text(t('see_full_calendar'));
            this.refreshConfirmState();
        }
    }

    /**
     * Lazy-fetch the full datesAvailability list when toggling to the
     * full calendar pane after a batched quick load. No-op if already loaded.
     */
    ensureDatesLoaded(cb) {
        if (this.datesLoaded) { cb(); return; }
        const $ = this.$;
        const $spinner = this.$spinner.show();
        $.ajax({
            type: 'GET',
            headers: {'X-CSRF-Token': this.data.token},
            url: this.data.apiRoutes.datesAvailability,
            data: $.extend({}, this.getQueryOptions()),
            complete: function () { $spinner.hide(); },
            success: $.proxy(function(dates) {
                this.dates = Array.isArray(dates) ? dates : [];
                this.datesLoaded = true;
                cb();
            }, this),
            error: () => cb()
        });
    }

    refreshConfirmState() {
        if (this.date && this.startAt) {
            this.$confirm.removeAttr('disabled');
        } else {
            this.$confirm.attr('disabled', 'disabled');
        }
    }

    getAvailableDates() {
        const $ = this.$;
        const $spinner = this.$spinner.show();
        const queryOptions = $.extend({}, this.getQueryOptions());

        // Quick view: single batched call. Falls back to legacy per-day loop
        // if the deployment hasn't shipped the new endpoint yet.
        if (this.quickEnabled && this.data.apiRoutes.nextDaysSlots) {
            this.loadQuickSlotsBatched();
            return;
        }

        $.ajax({
            type: 'GET',
            headers: {'X-CSRF-Token': this.data.token},
            url: this.data.apiRoutes.datesAvailability,
            data: queryOptions,
            success: $.proxy(function(dates) {
                this.dates = dates;
                this.datesLoaded = true;
                if (dates.length === 0) {
                    $spinner.hide();
                    this.noDatesAvailable();
                    return;
                }
                if (this.quickEnabled) {
                    this.loadQuickSlotsLegacy(dates.slice(0, QUICK_DAYS_LIMIT));
                } else {
                    $spinner.hide();
                    this.renderFlatpickr(dates);
                    this.flatpickrRendered = true;
                }
            }, this),
            error: $.proxy(function() {
                $spinner.hide();
                this.noDatesAvailable();
            }, this)
        });
    }

    /**
     * Single-call path. Server returns per-day { date, openFrom, openTo, available[] }
     * for the next N days that have slots — no need for datesAvailability + N×hours.
     * The full-calendar pane still calls datesAvailability lazily on toggle.
     */
    loadQuickSlotsBatched() {
        const $ = this.$;
        const queryOptions = $.extend({}, this.getQueryOptions());
        queryOptions.maxDays = QUICK_DAYS_LIMIT;

        $.ajax({
            type: 'GET',
            headers: {'X-CSRF-Token': this.data.token},
            url: this.data.apiRoutes.nextDaysSlots,
            data: queryOptions,
            success: $.proxy(function(daysData) {
                if (!Array.isArray(daysData) || daysData.length === 0) {
                    this.$spinner.hide();
                    this.quickDays = [];
                    this.renderQuickDays();
                    return;
                }
                this.quickDays = daysData.map(d => ({
                    date:     d.date,
                    openFrom: d.openFrom || null,
                    openTo:   d.openTo   || null,
                    slots:   (d.available || []).map(s => ({
                        startAt:   s.startAt,
                        endAt:     s.endAt,
                        available: true
                    }))
                }));
                // Mirror dates so the lazy flatpickr render has them too
                this.dates = this.quickDays.map(d => d.date);
                this.renderQuickDays();
                this.$spinner.hide();
            }, this),
            error: $.proxy(function() {
                this.$spinner.hide();
                this.noDatesAvailable();
            }, this)
        });
    }

    /** Legacy per-day fanout, retained as a fallback. */
    loadQuickSlotsLegacy(dates) {
        const promises = dates.map(date => this.fetchSlotsForDate(date));
        Promise.all(promises).then(slotsByDay => {
            this.quickDays = dates.map((date, i) => ({
                date,
                slots: Array.isArray(slotsByDay[i]) ? slotsByDay[i] : []
            }));
            this.renderQuickDays();
            this.$spinner.hide();
        }).catch(err => {
            console.warn('Failed to load quick slots:', err);
            this.$spinner.hide();
            this.$quickEmpty.show();
        });
    }

    fetchSlotsForDate(date) {
        const $ = this.$;
        const queryOptions = {
            ...this.getQueryOptions(),
            checkDate: date
        };
        return new Promise((resolve, reject) => {
            $.ajax({
                type: 'GET',
                headers: {'X-CSRF-Token': this.data.token},
                url: this.data.apiRoutes.hoursAvailability,
                data: queryOptions,
                success: resolve,
                error: reject
            });
        });
    }

    computeDefaultFromHour() {
        const now = new Date();
        now.setMinutes(now.getMinutes() + 15);
        let h = now.getHours();
        // Round UP to nearest 15-min increment so we don't surface past times
        let m = Math.ceil(now.getMinutes() / 15) * 15;
        if (m === 60) { h += 1; m = 0; }
        if (h > HOUR_GRID_MAX) { h = HOUR_GRID_MAX; m = 0; }
        if (h < HOUR_GRID_MIN) { h = HOUR_GRID_MIN; m = 0; }
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }

    buildHourMinuteGrids() {
        const $ = this.$;

        // Inline `style="..."` with !important beats every host-page CSS
        // rule we can plausibly fight. We hit a real prod bug where mobile
        // Safari kept right-aligning the grid even with !important in our
        // stylesheet — the host page likely had a higher-specificity
        // override-with-!important. Inline-style+!important is the floor
        // we can't be undercut on (only an inline-style+!important on the
        // host can beat it, and host inline styles can't target our
        // dynamically-rendered cells).
        const gridStyle =
            'display: grid !important;' +
            'grid-template-columns: repeat(4, minmax(0, 1fr)) !important;' +
            'gap: 6px !important;' +
            'width: 100% !important;' +
            'max-width: 100% !important;' +
            'box-sizing: border-box !important;' +
            'margin-top: 0 !important;' +
            'margin-bottom: 0 !important;' +
            'margin-left: 0 !important;' +
            'margin-right: 0 !important;' +
            'padding-top: 0 !important;' +
            'padding-bottom: 0 !important;' +
            'padding-left: 0 !important;' +
            'padding-right: 0 !important;' +
            'text-align: center !important;' +
            'direction: ltr !important;' +
            'justify-items: stretch !important;' +
            'justify-content: start !important;' +
            'float: none !important;';
        const cellStyle =
            'width: 100% !important;' +
            'max-width: 100% !important;' +
            'min-width: 0 !important;' +
            'box-sizing: border-box !important;' +
            'margin-top: 0 !important;' +
            'margin-bottom: 0 !important;' +
            'margin-left: 0 !important;' +
            'margin-right: 0 !important;' +
            'float: none !important;' +
            'justify-self: stretch !important;' +
            'text-align: center !important;';

        const hoursHtml = [];
        for (let h = HOUR_GRID_MIN; h <= HOUR_GRID_MAX; h++) {
            const v = String(h).padStart(2, '0');
            hoursHtml.push(`<button type="button" class="bw-quick-from-cell" data-value="${h}" style="${cellStyle}">${v}</button>`);
        }
        this.$fromHours.attr('style', gridStyle).html(hoursHtml.join(''));

        const minsHtml = MINUTE_GRID.map(m => {
            const v = String(m).padStart(2, '0');
            return `<button type="button" class="bw-quick-from-cell" data-value="${m}" style="${cellStyle}">${v}</button>`;
        });
        this.$fromMinutes.attr('style', gridStyle).html(minsHtml.join(''));
    }

    highlightFromGrid() {
        if (!this.$fromHours) return;
        const $ = this.$;
        $('.bw-quick-from-cell', this.$fromHours).removeClass('active');
        $('.bw-quick-from-cell', this.$fromMinutes).removeClass('active');
        if (!this.fromHour) return;
        const h = parseInt(this.fromHour.slice(0, 2), 10);
        const m = parseInt(this.fromHour.slice(3, 5), 10);
        $(`.bw-quick-from-cell[data-value="${h}"]`, this.$fromHours).addClass('active');
        $(`.bw-quick-from-cell[data-value="${m}"]`, this.$fromMinutes).addClass('active');
    }

    toggleFromPop(open) {
        if (!this.$fromPop) return;
        if (open) {
            // Portal both the panel AND the backdrop to a sibling of <body>
            // the first time we open. We keep them inside a wrapper that
            // re-applies the `.booking-widget` class so all scoped CSS
            // (which uses `.booking-widget X` selectors) keeps matching.
            //
            // Why: a transformed / will-change / filter / contain ancestor
            // on the host page creates a new containing block for
            // position:fixed (so the backdrop wouldn't cover the full
            // viewport) AND a new stacking context (so the panel's z-index
            // is trapped below other host-page chrome — which is why the
            // backdrop ends up above the panel even though the z-index
            // values say otherwise). Hoisting both to body fixes both.
            if (!this._portalContainer) {
                const portalEl = document.createElement('div');
                portalEl.className = 'booking-widget bw-quick-from-portal';
                document.body.appendChild(portalEl);
                if (this.$fromBackdrop && this.$fromBackdrop[0]) portalEl.appendChild(this.$fromBackdrop[0]);
                if (this.$fromPop && this.$fromPop[0])           portalEl.appendChild(this.$fromPop[0]);
                this._portalContainer = portalEl;
            }
            this.$fromPop.removeAttr('hidden');
            this.$fromBackdrop.removeAttr('hidden');
            this.$fromBtn.addClass('is-open');
            this.highlightFromGrid();
            this.positionFromPop();
            this._docKeyHandler = (ev) => {
                if (ev.key === 'Escape') this.toggleFromPop(false);
            };
            document.addEventListener('keydown', this._docKeyHandler);
            this._docRepositionHandler = () => this.positionFromPop();
            window.addEventListener('resize', this._docRepositionHandler);
            window.addEventListener('scroll', this._docRepositionHandler, true);
        } else {
            this.$fromPop.prop('hidden', true);
            this.$fromBackdrop.prop('hidden', true);
            this.$fromBtn.removeClass('is-open');
            if (this._docKeyHandler) {
                document.removeEventListener('keydown', this._docKeyHandler);
                this._docKeyHandler = null;
            }
            if (this._docRepositionHandler) {
                window.removeEventListener('resize', this._docRepositionHandler);
                window.removeEventListener('scroll', this._docRepositionHandler, true);
                this._docRepositionHandler = null;
            }
        }
    }

    /**
     * Center the popover horizontally within the booking widget root,
     * anchor it vertically just below the trigger button.
     *
     * The popover is portalled into <body> as `position: fixed`, so we
     * write viewport coordinates directly. No anchor math needed —
     * <body> isn't a transformed ancestor on any host we care about.
     */
    positionFromPop() {
        if (!this.$fromPop || this.$fromPop.prop('hidden')) return;
        const pop = this.$fromPop[0];
        const btn = this.$fromBtn[0];
        if (!pop || !btn) return;

        const widgetEl = this.$widgetRoot && this.$widgetRoot[0]
            ? this.$widgetRoot[0]
            : document.documentElement;
        const widgetRect = widgetEl.getBoundingClientRect();
        const btnRect = btn.getBoundingClientRect();

        // Reset before measuring so width reflects content, not previous clamp.
        pop.style.left = '-9999px';
        pop.style.top  = '0px';
        const popRect = pop.getBoundingClientRect();
        const popW = popRect.width;
        const popH = popRect.height;

        const margin = 12;

        // Horizontal: center within widget bounds (not viewport).
        let left = widgetRect.left + (widgetRect.width - popW) / 2;
        if (left < margin) left = margin;
        if (left + popW + margin > window.innerWidth) {
            left = Math.max(margin, window.innerWidth - popW - margin);
        }

        // Vertical: just below the button; flip above if not enough room.
        let top = btnRect.bottom + 8;
        if (top + popH + margin > window.innerHeight && btnRect.top - popH - 8 >= margin) {
            top = btnRect.top - popH - 8;
        }

        pop.style.left = left + 'px';
        pop.style.top  = top + 'px';
    }

    setFromHour(value) {
        this.fromHour = value && /^\d{1,2}:\d{2}/.test(value) ? value.slice(0, 5) : null;

        if (this.fromHour) {
            this.$fromText.text(t('from_time') + ' ' + this.fromHour);
            this.$fromBtn.addClass('is-active');
            if (this.$fromClear) this.$fromClear.show();
        } else {
            this.$fromText.text(t('from_any_time'));
            this.$fromBtn.removeClass('is-active');
            if (this.$fromClear) this.$fromClear.hide();
        }

        // Clear any current selection — chips may have been re-positioned/removed
        this.$('.bw-quick-chip', this.$quickList).removeClass('active');
        this.$('.bw-quick-day-row', this.$quickList).removeClass('is-selected');
        this.$quickList.removeClass('has-selection');
        this.date = undefined;
        this.startAt = undefined;
        this.endAt = undefined;
        this.$confirm.appendTo(this.$calendarRow);
        this.refreshConfirmState();

        this.renderQuickDays();
    }

    isToday(dateStr) {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return false;
        const now = new Date();
        return d.getFullYear() === now.getFullYear()
            && d.getMonth()    === now.getMonth()
            && d.getDate()     === now.getDate();
    }

    currentTimeInMinutes() {
        const now = new Date();
        return now.getHours() * 60 + now.getMinutes();
    }

    chipsForDay(day) {
        const isTodayDay = this.isToday(day.date);
        const nowMin = this.currentTimeInMinutes();
        // For today, never surface slots that have already started.
        const passesTodayFilter = (slot) => {
            if (!isTodayDay) return true;
            return this.toMinutes(slot.startAt) >= nowMin;
        };

        if (this.fromHour) {
            const fromMin = this.toMinutes(this.fromHour);
            if (isNaN(fromMin)) return [];
            return day.slots
                .filter(s => s.available
                          && this.toMinutes(s.startAt) >= fromMin
                          && passesTodayFilter(s))
                .slice()
                .sort((a, b) => this.toMinutes(a.startAt) - this.toMinutes(b.startAt))
                .slice(0, 3)
                .map(s => ({...s, bucket: null}));
        }

        // Bucket mode: working-hours window comes from server (openFrom/openTo)
        // when available; otherwise fall back to inferring from the FULL slot
        // list. Usable slots are filtered by today-clip.
        const usable = day.slots.filter(passesTodayFilter);
        const buckets = this.bucketSlotsForDay(usable, day.slots, day.openFrom, day.openTo);
        return ['morning', 'midday', 'evening']
            .filter(b => buckets[b])
            .map(b => ({...buckets[b], bucket: b}));
    }

    renderQuickDays() {
        this.$quickList.empty();
        const mode = this.fromHour ? 'chrono' : 'bucket';
        const rows = [];
        for (const day of this.quickDays) {
            const chips = this.chipsForDay(day);
            if (chips.length === 0) continue;
            const dateLabel = this.formatDateLabel(day.date);
            rows.push(renderQuickSlotRow({date: day.date, dateLabel, chips, mode}));
        }
        if (rows.length === 0) {
            this.$quickEmpty.show();
            if (this.$seeMoreRow) this.$seeMoreRow.hide();
            if (this.$seeCalRow) this.$seeCalRow.hide();
            return;
        }
        this.$quickEmpty.hide();
        this.$quickList.html(rows.join(''));

        // Bottom-row state. Only show "See full calendar" at the bottom AFTER
        // the user has expanded via "See more days" (i.e., the see-more feature
        // was relevant on this list). Short lists rely on the top toggle only.
        if (this.$seeMoreRow && this.$seeCalRow) {
            if (rows.length > QUICK_DAYS_INITIAL) {
                this.$quickList.addClass('is-collapsed');
                this.$seeMoreRow.css('display', 'flex');
                this.$seeCalRow.hide();
            } else {
                this.$quickList.removeClass('is-collapsed');
                this.$seeMoreRow.hide();
                this.$seeCalRow.hide();
            }
        }
    }

    /**
     * Split the day's working-hours range into three equal thirds. The window
     * comes from `openFrom`/`openTo` when supplied (server provides these on
     * the new nextDaysSlots endpoint), otherwise inferred from `baseSlots`
     * (full slot list, available + unavailable). Pick the earliest available
     * slot in each third from `usable`.
     */
    bucketSlotsForDay(usable, baseSlots, openFrom, openTo) {
        if (!usable || usable.length === 0) return {};
        let dayMin, dayMax;
        const fromMin = this.toMinutes(openFrom);
        const toMin   = this.toMinutes(openTo);
        if (!isNaN(fromMin) && !isNaN(toMin) && toMin > fromMin) {
            dayMin = fromMin;
            dayMax = toMin;
        } else {
            const base = baseSlots && baseSlots.length ? baseSlots : usable;
            const allTimes = base.map(s => this.toMinutes(s.startAt)).filter(n => !isNaN(n));
            if (allTimes.length === 0) return {};
            dayMin = Math.min.apply(null, allTimes);
            dayMax = Math.max.apply(null, allTimes);
        }
        const span = Math.max(1, dayMax - dayMin);
        const third1End = dayMin + span / 3;
        const third2End = dayMin + (2 * span) / 3;

        const buckets = {};
        const sortedAvailable = usable
            .filter(s => s.available)
            .slice()
            .sort((a, b) => this.toMinutes(a.startAt) - this.toMinutes(b.startAt));

        for (const slot of sortedAvailable) {
            const m = this.toMinutes(slot.startAt);
            let bucket;
            if (m < third1End) bucket = 'morning';
            else if (m < third2End) bucket = 'midday';
            else bucket = 'evening';
            if (!buckets[bucket]) buckets[bucket] = slot;
        }
        return buckets;
    }

    toMinutes(timeStr) {
        if (!timeStr || typeof timeStr !== 'string') return NaN;
        const parts = timeStr.split(':').map(Number);
        if (parts.length < 2 || parts.some(isNaN)) return NaN;
        return parts[0] * 60 + parts[1];
    }

    formatDateLabel(dateStr) {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        const lng = this.data.lng || 'en';
        // en-GB instead of en-US so the order is "Sat 9 May" (day before
        // month) — matches every other locale we ship and what the user
        // expects in this product.
        const localeMap = { ro: 'ro-RO', en: 'en-GB' };
        const locale = localeMap[lng] || lng;
        // Two-line layout: weekday on top, day+month below. Easier to scan
        // than a single dense string. We use formatToParts so the split
        // works in every locale (en uses comma, ro uses comma too, hu uses
        // dots/spaces). HTML is fine here — the consumer (templates.js)
        // injects this via innerHTML, not textContent.
        const weekday = d.toLocaleDateString(locale, { weekday: 'short' });
        const dayMonth = d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
        // Strip trailing punctuation that some locales attach to weekdays
        // ("lun." in fr, "lun.," in some intl variants) — looks cleaner
        // stacked on its own line.
        const cleanWeekday = weekday.replace(/[.,]+$/, '');
        const cleanDayMonth = dayMonth.replace(/[.,]+$/, '');
        return `<span class="bw-quick-day-weekday">${cleanWeekday}</span>` +
               `<span class="bw-quick-day-datemonth">${cleanDayMonth}</span>`;
    }

    selectQuickChip({$chip, date, startAt, endAt}) {
        const $ = this.$;
        const wasActive = $chip.hasClass('active');

        // Reset previous selection state
        $('.bw-quick-chip', this.$quickList).removeClass('active');
        $('.bw-quick-day-row', this.$quickList).removeClass('is-selected');
        this.$quickList.removeClass('has-selection');

        if (wasActive) {
            // Toggle off — clear selection
            this.date = undefined;
            this.startAt = undefined;
            this.endAt = undefined;
            // Restore confirm to its bottom position
            this.$confirm.appendTo(this.$calendarRow);
            // Restore "see more days" if the list was still collapsed (user hadn't expanded)
            if (this.$seeMoreRow && this.$quickList.hasClass('is-collapsed')) {
                this.$seeMoreRow.css('display', 'flex');
            }
        } else {
            $chip.addClass('active');
            this.date = date;
            this.startAt = startAt;
            this.endAt = endAt;
            // Mark the parent day-row as the focused selection
            const $row = $chip.closest('.bw-quick-day-row');
            $row.addClass('is-selected');
            this.$quickList.addClass('has-selection');
            // Move the confirm button right under the selected row
            this.$confirm.insertAfter($row);
            // Hide "see more days" once the user has picked a slot
            if (this.$seeMoreRow) this.$seeMoreRow.hide();
        }

        this.refreshConfirmState();
    }

    getQueryOptions() {
        const queryOptions = {
            locationId: this.state.selectedLocation > 0 ? this.state.selectedLocation : undefined,
            employeeId: this.state.selectedEmployee > 0 ? this.state.selectedEmployee : undefined,
        };
        if (isNonNullObject(this.state.selectedSubscription)) {
            queryOptions.subscriptionId = this.state.selectedSubscription.id;
            delete queryOptions.serviceId;
        } else {
            queryOptions.serviceId = Array.isArray(this.state.selectedServices) && this.state.selectedServices.length
                ? this.state.selectedServices
                : undefined;
        }
        // Ora pe care chiar ea o tine (rezervarea temporara) nu i se ascunde ei din lista.
        queryOptions.clientRef = this.state.clientRef || undefined;
        Object.keys(queryOptions).forEach(key => {
            if (queryOptions[key] === undefined || queryOptions[key] === null) {
                delete queryOptions[key];
            }
        });
        return queryOptions;
    }

    renderFlatpickr(dates) {
        const $ = this.$;
        const ctx = this;
        const options = $.extend({}, {
            inline: true,
            locale: this.data.lng || 'en',
            enable: dates.map(date => ({from: date, to: date})),
            maxDate: dates[dates.length - 1],
            minDate: dates[0],
            onChange: function (selectedDates, dateStr) {
                ctx.date = dateStr;
                ctx.startAt = '';
                ctx.endAt = '';
                ctx.refreshConfirmState();
                ctx.renderBookings(dateStr);
            },
            defaultDate: this.state.date ? this.state.date : undefined,
            monthSelectorType: 'static'
        });
        $('.flat-picker-container', this.$calendarPane).flatpickr(options);
    }

    renderBookings(dateStr) {
        const $ = this.$;
        const $spinner = this.$spinner.show();
        const ctx = this;
        const queryOptions = {
            ...this.getQueryOptions(),
            ...(dateStr && {checkDate: dateStr})
        };
        $.ajax({
            type: 'GET',
            headers: {'X-CSRF-Token': this.data.token},
            url: this.data.apiRoutes.hoursAvailability,
            data: queryOptions,
            complete: function () { $spinner.hide(); },
            success: $.proxy(function(slots) {
                const $bookingList = $('.booking-list-container', this.$calendarPane);
                $('.booking-list-title', this.$calendarPane).hide();
                $bookingList.empty();
                $.each(slots, function(index, obj) {
                    obj.index = index;
                    const $currentSlot = $(renderTimeSlot(obj));
                    $currentSlot.on('click', () => ctx.selectSlot(obj));
                    if (obj.available) {
                        $bookingList.append($currentSlot);
                    }
                });
                if ($('.booking-list-container button', this.$calendarPane).length === 0) {
                    $bookingList.append(
                        '<p style="grid-column-start: 1; grid-column-end: 5; text-align: center; color: #e97246;">Nu s-a găsit o oră disponibilă<br>Vă rugăm alegeți o altă zi.</p>'
                    );
                }

                const stepsHolder = $bookingList.closest('.booking-steps-holder')[0];
                if (stepsHolder) {
                    const offset = $bookingList[0].getBoundingClientRect().top - stepsHolder.getBoundingClientRect().top;
                    stepsHolder.scrollTo({top: stepsHolder.scrollTop + offset, behavior: 'smooth'});
                }
            }, this)
        });
    }

    selectSlot({startAt, endAt, available, index}) {
        if (available) {
            this.startAt = startAt;
            this.endAt = endAt;
            const $list = $('.booking-list-container', this.$calendarPane);
            $('button', $list).removeClass('active');
            $(`button[data-id="${index}"]`, $list).addClass('active');
            this.refreshConfirmState();
        }
    }

    next() {
        if (!(this.date && this.startAt) || this.holding) return;
        const slot = {date: this.date, startAt: this.startAt, endAt: this.endAt};
        const proceed = () => {
            this.state.date = slot.date;
            this.state.startAt = slot.startAt;
            this.state.endAt = slot.endAt;
            this.emit('calendar_selected', {date: slot.date, startAt: slot.startAt});
        };
        // Clasele nu ocupa ora unui angajat ca o programare: fara tinere.
        if (this.state.isClass) { proceed(); return; }

        // Rezervarea temporara: tinem ora INAINTE sa mergem mai departe (utils/slotHold.js). Daca
        // tocmai a luat-o altcineva, clienta afla acum, nu dupa ce si-a completat datele.
        this.holding = true;
        this.$confirm.attr('disabled', 'disabled');
        this.$spinner.show();
        holdSlot(this.api, this.data.apiRoutes, this.state, {
            employeeId: this.state.selectedEmployee > 0 ? this.state.selectedEmployee : 0,
            locationId: this.state.selectedLocation > 0 ? this.state.selectedLocation : 0,
            ...slot
        }).then((res) => {
            this.holding = false;
            this.$spinner.hide();
            if (res && res.taken) {
                this.markTaken(slot.date, slot.startAt);
                this.clearSelection();
                this.showSlotNotice(res.message || t('slot_just_taken'));
                this.pollSlots(true); // si alte ore s-ar fi putut ocupa intre timp
                return;
            }
            this.refreshConfirmState();
            proceed();
        });
    }

    // ---- Rezervarea temporara: orele luate intre timp de altcineva ----------------------------

    startSlotPolling() {
        this.stopSlotPolling();
        this.pollTimer = setInterval(() => this.pollSlots(), SLOT_POLL_MS);
    }

    stopSlotPolling() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    /** Intreaba din nou serverul ce ore sunt libere si le scoate din lista pe cele luate intre timp. */
    pollSlots(force = false) {
        const $ = this.$;
        // Pasul nu mai e pe ecran (s-a mers mai departe): oprim. Se reporneste la urmatorul render().
        if (!this.$calendarRow || !document.body.contains(this.$calendarRow[0])) {
            this.stopSlotPolling();
            return;
        }
        if (this.polling || (!force && (document.hidden || this.holding))) return;

        if (this.viewMode === 'quick') {
            if (!this.quickEnabled || !this.data.apiRoutes.nextDaysSlots || !this.quickDays.length) return;
            this.polling = true;
            $.ajax({
                type: 'GET',
                headers: {'X-CSRF-Token': this.data.token},
                url: this.data.apiRoutes.nextDaysSlots,
                data: {...this.getQueryOptions(), maxDays: QUICK_DAYS_LIMIT},
                success: (days) => {
                    if (!Array.isArray(days)) return;
                    const keys = [];
                    days.forEach(d => (d.available || []).forEach(s => keys.push(d.date + '|' + s.startAt)));
                    this.applyFreshSlots(keys, null);
                },
                complete: () => { this.polling = false; }
            });
        } else if (this.date) {
            const date = this.date;
            this.polling = true;
            $.ajax({
                type: 'GET',
                headers: {'X-CSRF-Token': this.data.token},
                url: this.data.apiRoutes.hoursAvailability,
                data: {...this.getQueryOptions(), checkDate: date},
                success: (slots) => {
                    if (!Array.isArray(slots) || this.date !== date) return;
                    this.applyFreshSlots(slots.filter(s => s.available !== false).map(s => date + '|' + s.startAt), date);
                },
                complete: () => { this.polling = false; }
            });
        }
    }

    /**
     * Doar SCOATE orele care nu mai sunt libere (taiate, neapasabile) - cele eliberate apar la
     * urmatoarea incarcare normala: lista nu se muta sub degetul clientei. $onlyDate = pasul cu
     * calendar (orele unei singure zile); null = lista rapida pe mai multe zile.
     */
    applyFreshSlots(keys, onlyDate) {
        const $ = this.$;
        const fresh = new Set(keys);
        let lostSelected = false;
        const gone = [];
        if (onlyDate === null) {
            $('.bw-quick-chip:not(.bw-quick-chip--empty):not(.bw-quick-chip--taken)', this.$quickList).each((_, el) => {
                const $c = $(el);
                const date = String($c.attr('data-date')), start = String($c.attr('data-start'));
                if (!fresh.has(date + '|' + start)) {
                    if ($c.hasClass('active')) lostSelected = true;
                    gone.push([date, start]);
                }
            });
        } else {
            $('.booking-list-container button[data-start]:not(.bw-slot--taken)', this.$calendarPane).each((_, el) => {
                const $b = $(el);
                const start = String($b.attr('data-start'));
                if (!fresh.has(onlyDate + '|' + start)) {
                    if ($b.hasClass('active')) lostSelected = true;
                    gone.push([onlyDate, start]);
                }
            });
        }
        gone.forEach(([date, start]) => this.markTaken(date, start));
        if (lostSelected) {
            this.clearSelection();
            this.showSlotNotice(t('slot_just_taken'));
        }
    }

    markTaken(date, startAt) {
        const $ = this.$;
        $('.bw-quick-chip', this.$quickList)
            .filter((_, el) => el.getAttribute('data-date') === date && el.getAttribute('data-start') === startAt)
            .removeClass('active').addClass('bw-quick-chip--taken').prop('disabled', true);
        if (this.date === date) {
            $('.booking-list-container button', this.$calendarPane)
                .filter((_, el) => el.getAttribute('data-start') === startAt)
                .removeClass('active').addClass('bw-slot--taken').prop('disabled', true);
        }
        // si din datele listei rapide, ca o redesenare (filtru "de la ora") sa n-o readuca
        this.quickDays.forEach(d => {
            if (d.date === date) d.slots = d.slots.filter(s => s.startAt !== startAt);
        });
    }

    clearSelection() {
        const $ = this.$;
        $('.bw-quick-chip', this.$quickList).removeClass('active');
        $('.bw-quick-day-row', this.$quickList).removeClass('is-selected');
        if (this.$quickList) this.$quickList.removeClass('has-selection');
        $('.booking-list-container button', this.$calendarPane).removeClass('active');
        if (this.viewMode === 'quick') this.date = undefined; // in calendar ramane ziua aleasa
        this.startAt = undefined;
        this.endAt = undefined;
        this.$confirm.appendTo(this.$calendarRow);
        this.refreshConfirmState();
    }

    showSlotNotice(message) {
        // Alerta centrata cu „Am inteles” (utils/alertDialog.js), nu un text intr-un colt: pe desktop
        // textul de sus trecea neobservat si clienta nu intelegea de ce i-a disparut selectia.
        showSlotTakenAlert(message, this.$widgetRoot && this.$widgetRoot[0]);
    }

    noDatesAvailable() {
        this.$quickPane.hide();
        this.$calendarPane.hide();
        this.$toggle.hide();
        this.$confirm.hide();
        this.$noCal.show();
    }
};
